import React, { useState } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  InputGroup, InputRightElement, useToast
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import SelectorPerfilUsuario from 'components/PerfilUsuario/SelectorPerfilUsuario';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onSubmit?: (data: any) => void;
}

const tarifas = {
  m2: 50,
  m3: 80,
};

const SolicitudDemolicion: React.FC<Props> = ({ modo, data, onSubmit }) => {
  const toast = useToast();

  const [tipo, setTipo] = useState(data?.tipo || '');
  const [unidad, setUnidad] = useState(data?.unidad || 'm2');
  const [valor, setValor] = useState(data?.valor || '');
  const [costo, setCosto] = useState(data?.costo || 0);
  const [direccion, setDireccion] = useState(data?.direccion || '');
  const [lat, setLat] = useState(Number(data?.lat) || 19.4326);
  const [lng, setLng] = useState(Number(data?.lng) || -99.1332);
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const [perfilSeleccionado, setPerfilSeleccionado] = useState<any>(null);

const handlePerfil = (perfil: any) => {
    setPerfilSeleccionado(perfil);
  };

  const buscarDireccion = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: direccion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setLat(loc.lat());
        setLng(loc.lng());
        setDireccion(results[0].formatted_address);
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Verifica que la dirección sea válida.',
          status: 'error',
        });
      }
    });
  };

  const calcularCosto = () => {
    const tarifa = tarifas[unidad as keyof typeof tarifas] || 0;
    setCosto(valor * tarifa);
  };

  const handleGuardar = async () => {
    const payload = {
      tipo,
      unidad,
      valor,
      costo,
      direccion,
      lat,
      lng,
      responsable,
      observaciones,
      fechaRegistro: new Date()
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/obras/solicitud-demolicion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Error al guardar solicitud');

      toast({
        title: 'Éxito',
        description: 'Solicitud registrada correctamente.',
        status: 'success',
      });

      onSubmit?.(payload);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un problema al registrar la solicitud.',
        status: 'error',
      });
    }
  };

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <VStack spacing={4} align="stretch">
      {(modo === 'edit' || modo === 'new') ? (
        <>
        <SelectorPerfilUsuario onPerfilSeleccionado={handlePerfil} />
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={4}>
        
          <Input placeholder="Tipo de demolición" value={tipo} onChange={(e) => setTipo(e.target.value)} />
          <Select value={unidad} onChange={(e) => setUnidad(e.target.value)}>
            <option value="m2">Metros cuadrados</option>
            <option value="m3">Metros cúbicos</option>
          </Select>
          <Input type="number" placeholder="Cantidad" value={valor} onChange={(e) => { setValor(Number(e.target.value)); calcularCosto(); }} />
          </SimpleGrid>
          <Text><strong>Costo estimado:</strong> ${costo}</Text>

          <InputGroup>
            <Input placeholder="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={buscarDireccion}>Buscar</Button>
            </InputRightElement>
          </InputGroup>

          <Box h="200px" w="100%">
            <GoogleMap
              zoom={15}
              center={{ lat, lng }}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            >
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={(e) => {
                  setLat(e.latLng?.lat() || lat);
                  setLng(e.latLng?.lng() || lng);
                }}
              />
            </GoogleMap>
          </Box>

          <Input placeholder="Responsable" value={responsable} onChange={(e) => setResponsable(e.target.value)} />
          <Textarea placeholder="Observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />

          <Button colorScheme="green" onClick={handleGuardar}>
            {modo === 'new' ? 'Registrar' : 'Actualizar'}
          </Button>
        </>
      ) : (
        <>
          <SimpleGrid columns={2} spacing={4}>
            <Box>
              <Text><strong>Tipo:</strong> {tipo}</Text>
              <Text><strong>Unidad:</strong> {unidad}</Text>
              <Text><strong>Valor:</strong> {valor}</Text>
              <Text><strong>Costo:</strong> ${costo}</Text>
            </Box>
            <Box>
              <Text><strong>Responsable:</strong> {responsable}</Text>
              <Text><strong>Dirección:</strong> {direccion}</Text>
              {observaciones && <Text><strong>Observaciones:</strong> {observaciones}</Text>}
            </Box>
          </SimpleGrid>

          <Box h="200px" w="100%">
            <GoogleMap
              zoom={15}
              center={{ lat, lng }}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            >
              <Marker position={{ lat, lng }} />
            </GoogleMap>
          </Box>
        </>
      )}
    </VStack>
  );
};

export default SolicitudDemolicion;
